import React, { ReactElement, useState, useMemo, useCallback } from "react";
import {
  makeStyles,
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  InputAdornment,
  CircularProgress,
  Dialog,
  DialogContent,
  IconButton,
  useTheme,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import CloseIcon from "@material-ui/icons/Close";
import { RedirectUri, useEnumStrings } from "../../enumStrings";
import Loader from "../../common/Loader/Loader";
import { useHistory } from "react-router-dom";
import useGetChamberFromUserIdInAuth from "../../common/Hooks/useGetChamberFromUserIdInAuth";
import {
  useGetAllChamberMembersQuery,
  ChamberMemberReturn,
  ChamberMemberCategory,
  useSendLoginInfoEmailToChamberMemberMutation,
  useSetNewTemporaryChamberMemberPasswordMutation,
  useGetSampleLoginInfoEmailQuery,
  useGetAdminConsoleLoginEmailForChamberMemberQuery,
  LoginInfoEmailTypes,
} from "../../../graphql/types";
import { CSVLink } from "react-csv";
import { SampleLoginInfoEmailModal } from "../../common/SendLoginInformationModal";
import MemberCredentialsTable from "./MemberCredentialsTable";
import DownloadAllMemberLoginCredentialsModal, { DownloadFailureModal } from "./DownloadAllMemberLoginCredentialsModal";
import { useGetCategoryIdToHumanReadableName } from "../../common/Hooks/useGetCategoryIdToHumanReadableName";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ExportIcon from "../../../images/export.png";
import EmailOutLineRounded from "../../../images/email-outline-rounded.png";
import FilterIcon from "../../../images/filter.png";
import TickCircleIcon from "../../../images/tick-circle.png";
import { getLocalTimestamp } from "../Root/RootAdmin";
import VideoTutorialButton from "../../common/VideoTutorialButton";
import ExportButton from "../../common/ExportButton";
import { openInstructionPdf } from "../../common/openInstructionPdf";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import ComingSoonDialog from "../../common/ComingSoonDialog";
import theme from "../../../themes";

const useStyles = makeStyles(() => ({
  root: {
    minHeight: "100vh",
    overflowX: "hidden",
  },
  headerSection: {
    backgroundColor: theme.palette.primary.main,
    padding: "30px 40px",
    minHeight: "100px",
    "@media (max-width: 1024px)": {
      padding: "24px 30px",
    },
    "@media (max-width: 768px)": {
      padding: "20px 24px",
      minHeight: "auto",
    },
  },
  titleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "10px",
  },
  description: {
    fontSize: "18px",
    color: "rgba(255, 255, 255, 0.9)",
    marginTop: "8px",
    lineHeight: "1.5",
    "@media (max-width: 1024px)": {
      fontSize: "16px",
    },
    "@media (max-width: 768px)": {
      fontSize: "16px",
    },
    "@media (max-width: 600px)": {
      fontSize: "16px",
      lineHeight: "1.55",
    },
  },
  buttonContainer: {
    display: "flex",
    gap: "15px",
    flexWrap: "wrap",
    marginTop: "20px",
    "@media (max-width: 768px)": {
      gap: "10px",
      marginTop: "16px",
    },
    "@media (max-width: 600px)": {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "8px",
      "& > div": {
        width: "100%",
      },
      "& > div > a, & > div > a > button, & > div > button, & > button": {
        width: "100%",
        marginTop: 0,
      },
    },
  },
  downloadAllButton: {
    backgroundColor: "white",
    color: "#D4A353",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "10px 20px",
    textTransform: "none" as const,
    fontSize: "14px",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    "@media (max-width: 600px)": {
      width: "100%",
      padding: "10px 14px",
      fontSize: "13px",
      whiteSpace: "nowrap" as const,
      minHeight: "44px",
    },
  },
  emailAllButton: {
    backgroundColor: "white",
    color: "#D4A353",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "10px 20px",
    textTransform: "none" as const,
    fontSize: "14px",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    "@media (max-width: 600px)": {
      width: "100%",
      padding: "10px 14px",
      fontSize: "13px",
      whiteSpace: "nowrap" as const,
      minHeight: "44px",
    },
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "20px",
  },
  headerContent: {
    flex: 1,
  },
  title: {
    fontWeight: "bold",
    fontSize: "32px",
    color: "white",
    margin: 0,
    "@media (max-width: 1024px)": {
      fontSize: "28px",
    },
    "@media (max-width: 768px)": {
      fontSize: "24px",
    },
  },
  subtitle: {
    fontSize: "18px",
    color: "rgba(255, 255, 255, 0.9)",
    marginTop: "8px",
  },
  contentSection: {
    padding: "0 20px 20px 20px",
    backgroundColor: theme.palette.primary.main,
    minHeight: "calc(100vh - 200px)",
    "@media (max-width: 1500px)": {
      padding: "0 16px 16px 16px",
    },
    "@media (max-width: 768px)": {
      padding: "0 12px 12px 12px",
    },
    "@media (max-width: 480px)": {
      padding: "0 8px 8px 8px",
    },
  },
  contentWrapper: {
    background: "#FFFFFF",
    borderRadius: "16px",
    padding: "20px",
    minHeight: "70vh",
    "@media (max-width: 1500px)": {
      padding: "16px",
    },
    "@media (max-width: 768px)": {
      padding: "12px",
    },
    "@media (max-width: 480px)": {
      padding: "8px",
      borderRadius: "12px",
    },
  },
  filterAndStatsRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "20px",
    flexWrap: "nowrap",
    gap: "12px",
    "@media (max-width: 1400px)": {
      flexWrap: "wrap",
    },
    "@media (max-width: 1024px)": {
      flexDirection: "column",
      alignItems: "stretch",
      gap: "16px",
    },
  },
  filtersSubContainer: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flex: "1 1 auto",
    flexWrap: "nowrap",
    minWidth: 0,
    "@media (max-width: 1400px)": {
      flexWrap: "wrap",
    },
    "@media (max-width: 768px)": {
      width: "100%",
      gap: "8px",
      flexWrap: "wrap",
    },
  },
  statsBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "4px",
    whiteSpace: "nowrap",
    minWidth: "fit-content",
    "@media (max-width: 1024px)": {
      alignItems: "flex-start",
      width: "100%",
    },
  },
  statValue: {
    fontSize: "14px",
    color: "#000",
    fontWeight: 400,
  },
  statLabel: {
    fontSize: "14px",
    color: "#000",
    fontWeight: 400,
  },
  searchField: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    width: "340px",
    height: "45px",
    flexShrink: 1,
    minWidth: "240px",
    "@media (max-width: 1400px)": {
      width: "280px",
    },
    "@media (max-width: 1024px)": {
      width: "240px",
    },
    "@media (max-width: 768px)": {
      width: "100%",
      maxWidth: "100%",
      flexShrink: 0,
    },
    "& .MuiOutlinedInput-root": {
      height: "45px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: 500,
      padding: "8px 9px",
      "& fieldset": {
        borderColor: "#000",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#000",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#000 !important",
      },
    },
    "& .MuiOutlinedInput-input": {
      fontSize: "14px",
      fontWeight: 500,
      color: "#000",
      padding: "0",
      "&::placeholder": {
        color: "#9E9E9E",
        opacity: 1,
      },
    },
  },
  filterSelect: {
    backgroundColor: "#fff",
    borderRadius: "8px",
    width: "auto",
    minWidth: "170px",
    height: "45px",
    flexShrink: 1,
    "@media (max-width: 1024px)": {
      minWidth: "150px",
    },
    "@media (max-width: 768px)": {
      minWidth: "140px",
      flex: "1 1 auto",
      flexShrink: 0,
    },
    "&:focus, &:focus-visible": {
      outline: "none",
    },
    "& .MuiOutlinedInput-root": {
      height: "45px",
      borderRadius: "8px",
      fontSize: "14px",
      fontWeight: 500,
      "& fieldset": {
        borderColor: "#000",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#000",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#000 !important",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: "#000 !important",
      },
    },
    "& .MuiSelect-select": {
      paddingTop: "12px",
      paddingBottom: "12px",
      paddingRight: "32px",
      color: "#000",
      fontSize: "14px",
      fontWeight: 500,
      display: "flex",
      alignItems: "center",
      gap: "10px",
      "&:focus": {
        backgroundColor: "transparent",
        outline: "none",
      },
    },
    "& .MuiSelect-icon": {
      display: "none",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "8px",
      borderColor: "#000 !important",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#000 !important",
    },
  },
  filterSelectMenu: {
    border: "1px solid #000",
    borderRadius: "8px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
    "& .MuiList-root": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      fontSize: "14px",
      fontWeight: 500,
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
      "&.Mui-selected": {
        backgroundColor: "#e0e0e0",
        "&:hover": {
          backgroundColor: "#d5d5d5",
        },
      },
    },
  },
  clearAllFiltersButton: {
    backgroundColor: (theme.palette as any).delete.main,
    color: "#FFFFFF",
    fontWeight: 500,
    borderRadius: "22px",
    padding: "8px 12px",
    textTransform: "none",
    fontSize: "12px",
    fontFamily: "Montserrat, sans-serif",
    width: "113px",
    height: "46px",
    minWidth: "113px",
    whiteSpace: "normal",
    boxShadow: "none",
    flexShrink: 0,
    lineHeight: "15px",
    textAlign: "center",
    "&:hover": {
      backgroundColor: "#D11920",
      boxShadow: "none",
    },
    "& .MuiButton-label": {
      whiteSpace: "normal",
      lineHeight: "15px",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
    },
    "@media (max-width: 768px)": {
      width: "100px",
      minWidth: "100px",
      fontSize: "11px",
      padding: "6px 10px",
      height: "40px",
    },
  },
  dialogCloseButton: {
    position: "absolute",
    top: "10px",
    right: "10px",
    color: "#EF4444",
  },
  successDialogPaper: {
    borderRadius: "16px",
    padding: "20px",
    maxWidth: "900px",
    position: "relative",
  },
  dialogIconContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
    marginTop: "20px",
  },
  dialogIcon: {
    width: "80px",
    height: "80px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "3px solid #22C55E",
    backgroundColor: "#DCFCE7",
  },
  dialogTitle: {
    fontSize: "24px",
    fontWeight: "bold",
    textAlign: "center",
    color: "#000",
  },
  successSection: {
    border: "2px dotted #E5E7EB",
    borderRadius: "12px",
    padding: "24px",
    backgroundColor: "#FEFCE8",
  },
  successSectionDark: {
    backgroundColor: "#F9FAFB",
  },
  successSectionTitle: {
    fontSize: "18px",
    fontWeight: "bold",
    marginBottom: "16px",
    color: "#000",
  },
  successSectionTitleGold: {
    color: theme.palette.primary.main,
  },
  successSectionTitleBlack: {
    color: "#000",
  },
  successButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "10px 32px",
    textTransform: "none",
    fontSize: "14px",
    "&:hover": {
      backgroundColor: "#D4A353",
    },
  },
  successButtonOutline: {
    backgroundColor: "white",
    color: "#000",
    fontWeight: "bold",
    borderRadius: "8px",
    padding: "10px 32px",
    textTransform: "none",
    fontSize: "14px",
    border: "2px solid #E5E7EB",
    "&:hover": {
      backgroundColor: "#F9FAFB",
    },
  },
  downloadConfirmDialogPaper: {
    borderRadius: "20px",
    maxWidth: "560px",
    width: "560px",
    position: "relative" as const,
    overflow: "visible",
    backgroundColor: "#FFFFFF",
    border: "1px solid #888888",
  },
  downloadConfirmCloseButton: {
    position: "absolute" as const,
    top: "10px",
    right: "10px",
    backgroundColor: (theme.palette as any).delete.main,
    color: "#FFFFFF",
    width: "28px",
    height: "28px",
    padding: 0,
    zIndex: 1,
    "&:hover": {
      backgroundColor: (theme.palette as any).delete.dark,
    },
  },
  downloadConfirmTitle: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 700,
    fontSize: "24px",
    color: "#000000",
    textAlign: "center" as const,
    marginBottom: "16px",
  },
  downloadConfirmBody: {
    fontFamily: "DM Sans, sans-serif",
    fontSize: "15px",
    color: "#333333",
    textAlign: "center" as const,
    lineHeight: "170%",
    marginBottom: "12px",
  },
  downloadConfirmTimestamp: {
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 700,
    fontSize: "16px",
    color: "#24272C",
    textAlign: "center" as const,
    marginBottom: "12px",
  },
  downloadConfirmWarning: {
    fontFamily: "DM Sans, sans-serif",
    fontSize: "14px",
    color: "#666666",
    textAlign: "center" as const,
    lineHeight: "160%",
    marginBottom: "24px",
  },
  downloadConfirmButtons: {
    display: "flex",
    justifyContent: "center",
    gap: "16px",
  },
  downloadConfirmBtn: {
    backgroundColor: theme.palette.primary.main,
    color: "#FFFFFF",
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 700,
    fontSize: "15px",
    borderRadius: "10px",
    padding: "12px 36px",
    textTransform: "none" as const,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  downloadCancelBtn: {
    backgroundColor: "#24272C",
    color: "#FFFFFF",
    fontFamily: "DM Sans, sans-serif",
    fontWeight: 700,
    fontSize: "15px",
    borderRadius: "10px",
    padding: "12px 36px",
    textTransform: "none" as const,
    "&:hover": {
      backgroundColor: "#000000",
    },
  },
}));

const convertArrayToCSV = (arr: any[]) => {
  if (!arr || !arr.length) return "";
  const keys = Object.keys(arr[0]);
  const header = keys.join(",") + "\n";
  const rows = arr.map(obj => keys.map(key => {
    let val = obj[key] ? obj[key].toString() : '';
    // escape double quotes and wrap in quotes if it contains comma or quotes
    if (val.search(/("|,|\n)/g) >= 0)
      val = `"${val.replace(/"/g, '""')}"`;
    return val;
  }).join(",")).join("\n");
  return header + rows;
};

const SendMemberCredentials = (): ReactElement => {
  const classes = useStyles();
  const enumStrings = useEnumStrings() as any;
  const history = useHistory();
  const theme = useTheme();
  const chamberInfo = useGetChamberFromUserIdInAuth();
  const { allCategories } = useGetCategoryIdToHumanReadableName();

  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [credentialsModalOpen, setCredentialsModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<ChamberMemberReturn | null>(null);
  const [showDownloadFailureModal, setShowDownloadFailureModal] = useState(false);
  const [comingSoonOpen, setComingSoonOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [downloadingCredentials, setDownloadingCredentials] = useState(false);
  const [emailSentSuccess, setEmailSentSuccess] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [downloadConfirmOpen, setDownloadConfirmOpen] = useState(false);
  const [downloadSuccessOpen, setDownloadSuccessOpen] = useState(false);

  // Mutations and queries for send email functionality
  const [sendLoginInfoEmailToChamberMember] = useSendLoginInfoEmailToChamberMemberMutation();
  const [setNewTemporaryChamberMemberPassword] = useSetNewTemporaryChamberMemberPasswordMutation();

  // Fetch the ACTUAL login email from auth system (Cognito) for the selected member
  const { data: adminConsoleLoginEmailData } = useGetAdminConsoleLoginEmailForChamberMemberQuery({
    variables: { chamberMemberId: selectedMember?.id || "" },
    skip: !selectedMember?.id,
    fetchPolicy: "network-only",
  });

  // Use Cognito email as source of truth, fall back to DB email
  const resolvedAdminEmail = adminConsoleLoginEmailData?.getAdminConsoleLoginEmailForChamberMember?.adminConsoleLoginEmail
    || selectedMember?.adminConsoleLoginEmail || "";

  // Query for email preview
  const { data: sampleEmailData } = useGetSampleLoginInfoEmailQuery({
    variables: {
      type: LoginInfoEmailTypes.BrandNew,
      chamberMemberId: selectedMember?.id || "",
    },
    skip: !selectedMember?.id,
  });

  const handleOpenCredentialsModal = useCallback((member: ChamberMemberReturn) => {
    setSelectedMember(member);
    setCredentialsModalOpen(true);
    setEmailSentSuccess(false);
    setNewPassword("");
  }, []);

  const handleCloseCredentialsModal = useCallback(() => {
    setCredentialsModalOpen(false);
    setSelectedMember(null);
    setEmailSentSuccess(false);
    setNewPassword("");
  }, []);

  // Handler for Send Email button
  const handleSendEmail = useCallback(async () => {
    if (!selectedMember) {
      toast.error("No member selected");
      return;
    }

    setSendingEmail(true);
    try {
      // Generate a temporary password first before sending the email
      const { data: passwordData } = await setNewTemporaryChamberMemberPassword({
        variables: {
          adminConsoleLoginEmail: resolvedAdminEmail,
          chamberMemberId: selectedMember.id || "",
        },
      });

      const password = passwordData?.setNewTemporaryChamberMemberPassword?.password || "";
      if (!password) {
        toast.error("Failed to generate credentials. Please try again.");
        setSendingEmail(false);
        return;
      }

      setNewPassword(password);

      const { data } = await sendLoginInfoEmailToChamberMember({
        variables: {
          adminConsoleLoginEmail: resolvedAdminEmail,
          chamberMemberId: selectedMember.id || "",
          type: LoginInfoEmailTypes.BrandNew,
        },
        refetchQueries: ["getAllChamberMembers"],
        awaitRefetchQueries: true,
      });

      if (data?.sendLoginInfoEmailToChamberMember?.loginInfoEmailSentSuccessfully) {
        setEmailSentSuccess(true);
        toast.success("Email sent successfully!");
      } else {
        toast.error("Failed to send email. Please try again.");
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setSendingEmail(false);
    }
  }, [selectedMember, sendLoginInfoEmailToChamberMember, setNewTemporaryChamberMemberPassword]);

  // Handler for Download Credentials button
  const handleDownloadCredentials = useCallback(async () => {
    if (!selectedMember) {
      toast.error("No member selected");
      return;
    }

    setDownloadingCredentials(true);
    try {
      // If password was already generated (e.g. via Send Email), reuse it
      let password = newPassword;
      if (!password) {
        const { data } = await setNewTemporaryChamberMemberPassword({
          variables: {
            adminConsoleLoginEmail: resolvedAdminEmail,
            chamberMemberId: selectedMember.id || "",
          },
        });
        password = data?.setNewTemporaryChamberMemberPassword?.password || "";
        if (password) {
          setNewPassword(password);
        }
      }

      if (password) {
        // Build CSV and trigger download programmatically to avoid race condition
        const emailHeader = enumStrings.sendLoginModal?.memberEmail || "Email";
        const passwordHeader = enumStrings.sendLoginModal?.memberPassword || "Password";
        const csvContent = `${emailHeader},${passwordHeader}\n${resolvedAdminEmail},${password}`;
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${selectedMember.name || "member"} member dashboard credentials.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success("Credentials downloaded!");
      } else {
        toast.error("Failed to generate credentials. Please try again.");
      }
    } catch (error) {
      console.error("Error downloading credentials:", error);
      toast.error("Failed to generate credentials. Please try again.");
    } finally {
      setDownloadingCredentials(false);
    }
  }, [selectedMember, setNewTemporaryChamberMemberPassword, newPassword, enumStrings]);

  // Handler for Preview Email button
  const handlePreviewEmail = useCallback(() => {
    setPreviewModalOpen(true);
  }, []);

  const handleClosePreviewModal = useCallback(() => {
    setPreviewModalOpen(false);
  }, []);

  // CSV data for download
  const csvData = useMemo(() => {
    if (!selectedMember || !newPassword) return [];
    return [
      [enumStrings.sendLoginModal?.memberEmail || "Email", enumStrings.sendLoginModal?.memberPassword || "Password"],
      [resolvedAdminEmail, newPassword],
    ];
  }, [selectedMember, newPassword, enumStrings, resolvedAdminEmail]);

  // Use useGetAllChamberMembersQuery instead of useGetChamberMembersQuery
  const { data, loading, error } = useGetAllChamberMembersQuery({
    variables: {
      chamberId: chamberInfo?.id || "",
    },
    skip: !chamberInfo?.id,
    fetchPolicy: "cache-and-network",
  });

  const allMembers = useMemo(() => {
    return (data?.getAllChamberMembers as ChamberMemberReturn[]) || [];
  }, [data]);

  const filteredMembers = useMemo(() => {
    return allMembers.filter((member) => {
      const matchesSearch = member.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());

      const matchesStatus =
        statusFilter === "all"
          ? true
          : statusFilter === "loggedIn"
            ? member.hasSignedUp
            : !member.hasSignedUp;

      const matchesCategory =
        categoryFilter === "all"
          ? true
          : member.categoryId && member.categoryId.includes(categoryFilter as ChamberMemberCategory);

      return matchesSearch && matchesStatus && matchesCategory;
    });
  }, [allMembers, searchTerm, statusFilter, categoryFilter]);

  const totalLogins = useMemo(() => {
    return allMembers.filter((m) => m.hasSignedUp).length;
  }, [allMembers]);

  const hasActiveFilters = searchTerm !== "" || statusFilter !== "all" || categoryFilter !== "all";

  const handleClearAllFilters = useCallback(() => {
    setSearchTerm("");
    setStatusFilter("all");
    setCategoryFilter("all");
  }, []);

  const handleDownload = async () => {
    try {
      if (filteredMembers.length === 0) {
        return;
      }
      const csvData = filteredMembers.map((member) => ({
        "Company Name": member.name,
        "Contact Name": member.adminConsoleContactName || "",
        "Contact Email": member.adminConsoleLoginEmail || "",
        "Status": member.hasSignedUp ? "Logged In" : "Never Logged In",
        "Last Sent": member.credentialsSentAtUtcMilli
          ? getLocalTimestamp(member.credentialsSentAtUtcMilli, true)
          : "Never",
      }));

      const csvString = convertArrayToCSV(csvData);

      const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", "member_credentials.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloadConfirmOpen(false);
      setDownloadSuccessOpen(true);
    } catch (e) {
      setDownloadConfirmOpen(false);
      setShowDownloadFailureModal(true);
    }
  };

  const handleManageMember = (member: ChamberMemberReturn) => {
    history.push(RedirectUri.updateAMemberChamberAdmin, {
      chamberMemberId: member.id,
      scrollToSection: "adminContact",
    });
  };

  if (loading && !data) {
    return <Loader />;
  }

  if (error) return <Typography color="error">{enumStrings.common.errorLoadingMembers}</Typography>;

  return (
    <div className={classes.root}>
      <ToastContainer />
      {/* Header Section with Golden Background */}
      <div className={classes.headerSection}>
        <div className={classes.titleRow}>
          <h1 className={classes.title}>{enumStrings.sendMemberCredentials.title}</h1>
        </div>
        <p className={classes.description}>
          {enumStrings.sendMemberCredentials.description}
        </p>
        <div className={classes.buttonContainer}>
          <VideoTutorialButton
            tutorialUrl="https://share.synthesia.io/embeds/videos/d6873d08-2e6f-497c-b9f2-1e9a97806ccb"
          />
          <ExportButton
            customOnClick={() => openInstructionPdf("Instruction on Sending Member Credentials")}
            icon={<InfoOutlinedIcon style={{ color: theme.palette.primary.main, marginRight: "10px", width: "24px", height: "24px" }} />}
            label={enumStrings.manageChamberEngage?.viewInstructions || "VIEW INSTRUCTIONS"}
            disablePadding
          />
          <Button
            className={classes.downloadAllButton}
            startIcon={<img src={ExportIcon} alt="Download" style={{ width: "20px", height: "20px", filter: "brightness(0) saturate(100%) invert(67%) sepia(67%) saturate(481%) hue-rotate(5deg) brightness(95%) contrast(87%)" }} />}
            onClick={() => setDownloadConfirmOpen(true)}
            variant="contained"
          >
            {enumStrings.sendMemberCredentials.downloadMemberCredentials}
          </Button>
          <Button
            className={classes.emailAllButton}
            startIcon={<img src={EmailOutLineRounded} alt="Email" style={{ width: "20px", height: "20px", filter: "brightness(0) saturate(100%) invert(67%) sepia(67%) saturate(481%) hue-rotate(5deg) brightness(95%) contrast(87%)" }} />}
            onClick={() => setShowDownloadModal(true)}
            variant="contained"
          >
            {enumStrings.sendMemberCredentials.emailAllMemberCredentials}
          </Button>
        </div>
      </div>

      {/* Content Section */}
      <div className={classes.contentSection}>
        <div className={classes.contentWrapper}>
          {/* Filters and Stats in same row */}
          <div className={classes.filterAndStatsRow}>
            <div className={classes.filtersSubContainer}>
              <TextField
                className={classes.searchField}
                placeholder={enumStrings.manageMemberCredentials.searchPlaceholder}
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon style={{ color: "#999" }} />
                    </InputAdornment>
                  ),
                }}
              />

              <Select
                className={classes.filterSelect}
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as string)}
                variant="outlined"
                displayEmpty
                style={{ color: statusFilter !== "all" ? "#000" : "#9E9E9E" }}
                startAdornment={
                  <InputAdornment position="start">
                    <img src={FilterIcon} alt="Filter" style={{ width: "18px", height: "18px", marginRight: "8px" }} />
                  </InputAdornment>
                }
                MenuProps={{
                  classes: { paper: classes.filterSelectMenu },
                  PaperProps: {
                    style: {
                      border: "1px solid #000",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    },
                  },
                  MenuListProps: {
                    style: {
                      padding: "4px 0",
                    },
                  },
                }}
              >
                <MenuItem disableRipple value="all" style={{ backgroundColor: statusFilter === "all" ? "#e0e0e0" : undefined }}>{statusFilter !== "all" ? enumStrings.manageMemberCredentials.clearStatus : enumStrings.manageMemberCredentials.filterByStatus}</MenuItem>
                <MenuItem disableRipple value="loggedIn" style={{ backgroundColor: statusFilter === "loggedIn" ? "#e0e0e0" : undefined }}>{enumStrings.manageMemberCredentials.chips.loggedIn}</MenuItem>
                <MenuItem disableRipple value="neverLoggedIn" style={{ backgroundColor: statusFilter === "neverLoggedIn" ? "#e0e0e0" : undefined }}>{enumStrings.manageMemberCredentials.chips.neverLoggedIn}</MenuItem>
              </Select>

              <Select
                className={classes.filterSelect}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value as string)}
                variant="outlined"
                displayEmpty
                style={{ color: categoryFilter !== "all" ? "#000" : "#9E9E9E" }}
                startAdornment={
                  <InputAdornment position="start">
                    <img src={FilterIcon} alt="Filter" style={{ width: "18px", height: "18px", marginRight: "8px" }} />
                  </InputAdornment>
                }
                MenuProps={{
                  classes: { paper: classes.filterSelectMenu },
                  PaperProps: {
                    style: {
                      border: "1px solid #000",
                      borderRadius: "8px",
                      boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                    },
                  },
                  MenuListProps: {
                    style: {
                      padding: "4px 0",
                    },
                  },
                }}
              >
                <MenuItem disableRipple value="all" style={{ backgroundColor: categoryFilter === "all" ? "#e0e0e0" : undefined }}>{categoryFilter !== "all" ? enumStrings.manageMemberCredentials.clearCategory : enumStrings.manageMemberCredentials.filterByCategory}</MenuItem>
                {allCategories && allCategories.map((category) => (
                  <MenuItem disableRipple key={category.id} value={category.id} style={{ backgroundColor: categoryFilter === category.id ? "#e0e0e0" : undefined }}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>

              <Button
                className={classes.clearAllFiltersButton}
                onClick={handleClearAllFilters}
                variant="contained"
              >
                <>
                  CLEAR ALL
                  <br />
                  FILTERS
                </>
              </Button>
            </div>

            {/* Stats on the right */}
            <div className={classes.statsBox}>
              <Typography className={classes.statValue}>
                {enumStrings.sendMemberCredentials.totalMemberLogIns} {totalLogins}/{allMembers.length}
              </Typography>
              <Typography className={classes.statLabel}>
                {enumStrings.sendMemberCredentials.percentage} {allMembers.length > 0 ? Math.round((totalLogins / allMembers.length) * 100) : 0}%
              </Typography>
            </div>
          </div>

          {/* Table */}
          <MemberCredentialsTable
            data={filteredMembers}
            onManageMember={handleManageMember}
            onOpenCredentialsModal={handleOpenCredentialsModal}
          />
        </div>
      </div>

      <ComingSoonDialog
        open={comingSoonOpen}
        onClose={() => setComingSoonOpen(false)}
      />

      {/* Modals */}
      {showDownloadModal && (
        <DownloadAllMemberLoginCredentialsModal
          open={showDownloadModal}
          handleClose={() => setShowDownloadModal(false)}
          numOfMembersNotLoggedInYet={allMembers.length - totalLogins}
          sampleMemberId={allMembers[0]?.id || ""}
        />
      )}

      {showDownloadFailureModal && (
        <DownloadFailureModal
          open={showDownloadFailureModal}
          handleClose={() => setShowDownloadFailureModal(false)}
        />
      )}

      {/* Download Confirmation Dialog */}
      <Dialog
        open={downloadConfirmOpen}
        onClose={() => setDownloadConfirmOpen(false)}
        classes={{ paper: classes.downloadConfirmDialogPaper }}
      >
        <IconButton
          className={classes.downloadConfirmCloseButton}
          onClick={() => setDownloadConfirmOpen(false)}
        >
          <CloseIcon style={{ fontSize: "18px" }} />
        </IconButton>
        <DialogContent style={{ padding: "40px 40px 32px 40px", textAlign: "center" }}>
          <div className={classes.downloadConfirmTitle}>
            {enumStrings.DownloadMemberCredentials.downloadConfirmation.title}
          </div>
          <div className={classes.downloadConfirmBody}>
            {enumStrings.DownloadMemberCredentials.downloadConfirmation.downloadForMembersPrefix}{" "}
            <strong>{filteredMembers.length}</strong>{" "}
            {filteredMembers.length !== 1
              ? enumStrings.DownloadMemberCredentials.downloadConfirmation.members
              : enumStrings.DownloadMemberCredentials.downloadConfirmation.member}{" "}
            {enumStrings.DownloadMemberCredentials.downloadConfirmation.downloadForMembersSuffix}
          </div>
          <div className={classes.downloadConfirmWarning}>
            {enumStrings.DownloadMemberCredentials.downloadConfirmation.message}
          </div>
          <div className={classes.downloadConfirmButtons}>
            <Button
              className={classes.downloadConfirmBtn}
              onClick={handleDownload}
              variant="contained"
            >
              {enumStrings.DownloadMemberCredentials.downloadConfirmation.confirmButton}
            </Button>
            <Button
              className={classes.downloadCancelBtn}
              onClick={() => setDownloadConfirmOpen(false)}
              variant="contained"
            >
              {enumStrings.DownloadMemberCredentials.downloadConfirmation.cancelButton}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Download Success Dialog */}
      <Dialog
        open={downloadSuccessOpen}
        onClose={() => setDownloadSuccessOpen(false)}
        classes={{ paper: classes.downloadConfirmDialogPaper }}
      >
        <IconButton
          className={classes.downloadConfirmCloseButton}
          onClick={() => setDownloadSuccessOpen(false)}
        >
          <CloseIcon style={{ fontSize: "18px" }} />
        </IconButton>
        <DialogContent style={{ padding: "40px 40px 32px 40px", textAlign: "center" }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: "20px" }}>
            <div style={{
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: "#00B621",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}>
              <img src={TickCircleIcon} alt="Success" style={{ width: "48px", height: "48px", filter: "brightness(0) invert(1)" }} />
            </div>
          </div>
          <div className={classes.downloadConfirmTitle}>
            {enumStrings.CSVDownloadNotification.downloadsuccess}
          </div>
          <div className={classes.downloadConfirmBody}>
            {enumStrings.CSVDownloadNotification.title}{" "}
            <strong>{filteredMembers.filter(m => !m.hasSignedUp).length}</strong>{" "}
            {enumStrings.CSVDownloadNotification.numOfMembersNotLoggedInYet}{" "}
            <strong>{enumStrings.CSVDownloadNotification.notLoggedInYet}</strong>{" "}
            {enumStrings.CSVDownloadNotification.activeMembershipStatus}
          </div>
          <div className={classes.downloadConfirmWarning}>
            {enumStrings.CSVDownloadNotification.downloadInfo}
          </div>
          <div className={classes.downloadConfirmButtons}>
            <Button
              className={classes.downloadConfirmBtn}
              onClick={handleDownload}
              variant="contained"
            >
              {enumStrings.CSVDownloadNotification.download}
            </Button>
            <Button
              className={classes.downloadCancelBtn}
              onClick={() => setDownloadSuccessOpen(false)}
              variant="contained"
            >
              {enumStrings.CSVDownloadNotification.close}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Preview Modal */}
      <SampleLoginInfoEmailModal
        open={previewModalOpen}
        handleClose={handleClosePreviewModal}
        emailHtml={sampleEmailData?.getSampleLoginInfoEmail?.emailHtml}
        modalType="memberTable"
        chamberName={chamberInfo?.name || ""}
        chamberEmail={chamberInfo?.supportContactEmail || ""}
        adminContactName={chamberInfo?.supportContactName || ""}
      />

      {/* New Temporary Password Generated Dialog */}
      {selectedMember && (
        <Dialog
          open={credentialsModalOpen}
          onClose={handleCloseCredentialsModal}
          classes={{ paper: classes.successDialogPaper }}
        >
          <IconButton
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              zIndex: 1,
              backgroundColor: theme.palette.error.main,
              color: "#fff",
              width: "28px",
              height: "28px",
            }}
            onClick={handleCloseCredentialsModal}
          >
            <CloseIcon style={{ fontSize: "18px" }} />
          </IconButton>
          <DialogContent style={{ padding: "20px 40px" }}>
            {emailSentSuccess ? (
              <>
                <div className={classes.dialogIconContainer}>
                  <div className={classes.dialogIcon}>
                    <img src={TickCircleIcon} alt="Success" style={{ width: "48px", height: "48px" }} />
                  </div>
                </div>
                <div className={classes.dialogTitle} style={{ marginBottom: "32px" }}>
                  {enumStrings.sendLoginModal?.memberTitle2 || "Login credentials have been emailed!"}
                </div>
                <div style={{ display: "flex", justifyContent: "center" }}>
                  <Button className={classes.successButton} onClick={handleCloseCredentialsModal}>
                    {enumStrings.shopLocal?.createShopLocalCampaign?.close || "Close"}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <div className={classes.dialogIconContainer}>
                  <div className={classes.dialogIcon}>
                    <img src={TickCircleIcon} alt="Success" style={{ width: "48px", height: "48px" }} />
                  </div>
                </div>
                <div className={classes.dialogTitle} style={{ marginBottom: "32px" }}>
                  {enumStrings.sendMemberCredentials.newTempPasswordGenerated}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "24px" }}>
                  {/* Manual Section */}
                  <div className={classes.successSection}>
                    <div className={classes.successSectionTitle} style={{ color: theme.palette.primary.main }}>
                      {enumStrings.sendMemberCredentials.manuallySendTitle}
                    </div>
                    <div style={{ fontSize: "14px", color: "#666", lineHeight: "1.6", marginBottom: "16px" }}>
                      {enumStrings.sendMemberCredentials.manuallySendDesc}
                    </div>
                    <Button
                      className={classes.successButton}
                      onClick={handleDownloadCredentials}
                      disabled={downloadingCredentials}
                    >
                      {downloadingCredentials ? (
                        <CircularProgress size={20} style={{ color: "white" }} />
                      ) : (
                        enumStrings.manageMembersPage.downloadCredentials
                      )}
                    </Button>
                  </div>

                  {/* Automatic Section */}
                  <div className={`${classes.successSection} ${classes.successSectionDark}`}>
                    <div className={classes.successSectionTitle}>
                      {enumStrings.sendMemberCredentials.automaticallySendTitle}
                    </div>
                    <div style={{ fontSize: "14px", color: "#666", lineHeight: "1.6", marginBottom: "16px" }}>
                      {enumStrings.sendMemberCredentials.automaticallySendDesc}
                    </div>
                    <div style={{ display: "flex", gap: "12px" }}>
                      <Button
                        className={classes.successButton}
                        onClick={handleSendEmail}
                        disabled={sendingEmail}
                      >
                        {sendingEmail ? (
                          <CircularProgress size={20} style={{ color: "white" }} />
                        ) : (
                          enumStrings.manageMembersPage.sendEmail
                        )}
                      </Button>
                      <Button
                        className={classes.successButtonOutline}
                        onClick={handlePreviewEmail}
                      >
                        {enumStrings.manageMembersPage.previewEmail}
                      </Button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}

      {/* Hidden CSV download link - kept for backwards compatibility */}
      <CSVLink
        data={csvData}
        filename={`${selectedMember?.name || "member"} member dashboard credentials.csv`}
        id="credentials-csv-download-button"
        style={{ display: "none" }}
      />
    </div>
  );
};

export default SendMemberCredentials;
